import {
  Box,
  Button,
  Card,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  Typography
} from '@mui/material'
import { ArrowBack } from '@mui/icons-material'
import { PriorityStatusButton } from '../PriorityStatusButton'
import { Comments } from '../../../../customComponents/comments/Comments'
import { CommentParentType } from '../../../../../models/Comment'
import { useEffect, useRef, useState } from 'react'
import {
  type PlanOrganizationTask,
  PlanOrganizationTaskStatus
} from '../../../../../models/PlanOrganizationTask'
import { AnnualPlanRole } from '../../../../../models/AnnualPlanRole'
import { PriorityActivityCard } from '../PriorityActivityCard'
import { type Priority } from '../../../../../models/Priority'
import { AcknowledgeCoachingTool } from '../../../../../services/AnnualPlanService'
import { toast } from 'react-toastify'
import { useParams, useSearchParams } from 'react-router-dom'
import { useAnnualPlanUserRole } from '../../../../../hooks/use-annualPlanUserRole'
import { PriorityTwoActivityName } from '../../../../../models/PriorityTwoActivityName'
import { CSDevelopmentChecklistList } from './CSDevelopmentChecklistList'
import { useUpdatePlanOrgTaskStatus } from '../../../../../hooks/use-updatePlanOrgTaskStatus'
import { OverviewStatusModals } from '../priorityFour/OverviewStatusModals'
import { Modal } from '../../../../customComponents/Modal'
import { LockedBanner } from '../../LockedBanner'
import { useAnnualPlanningNavigation } from '../../../../../hooks/use-annualPlanningNavigation'

interface Props {
  priority: Priority
  planOrganizationTask: PlanOrganizationTask
  onBack: () => void
  onPlanOrgTaskUpdate: (updatedTask: PlanOrganizationTask) => void
  implementationPhase: boolean
}

export const PriorityTwoOverview = (props: Props): JSX.Element => {
  const [searchParams, setSearchParams] = useSearchParams()
  const { planId, planOrgId } = useParams()
  const { userRole, isLoading, error } = useAnnualPlanUserRole(planId != null ? Number(planId) : 0)

  const [coachingToolAcknowledged, setCoachingToolAcknowledged] = useState(props.priority.acknowledgement ?? false)
  const [selectedPriorityActivity, setSelectedPriorityActivity] = useState<string | null>(null)
  const [showConfirmCheckCoachingCapacityToolModal, setShowConfirmCheckCoachingCapacityToolModal] = useState(false)
  const [focusCheckbox, setFocusCheckbox] = useState(false)
  const { handleNavigateToContract } = useAnnualPlanningNavigation()

  const {
    isUpdating,
    handleConfirmLockModal,
    updatePlanOrgTaskStatus,
    statusForLockModal,
    setStatusForLockModal,
    statusForSomeItemsLockModal,
    setStatusForSomeItemsLockModal,
    statusForSubmittedToInProgressModal,
    setStatusForSubmittedToInProgressModal,
    statusForSubmittedToAcceptedModal,
    setStatusForSubmittedToAcceptedModal,
    statusForSubmittedToNeedsRevisionModal,
    setStatusForSubmittedToNeedsRevisionModal,
    handleConfirmSubmittedToNeedsRevision,
    disableStatus,
    readOnly,
    trackingReadonly
  } = useUpdatePlanOrgTaskStatus({
    userRole,
    annualPlanId: Number(planId),
    planOrganizationTask: props.planOrganizationTask,
    onPlanOrgTaskUpdate: props.onPlanOrgTaskUpdate,
    priorityActivities: props.priority.activities,
    onBack: props.onBack,
    contractPhase: !props.implementationPhase
  })

  const coachingToolAcknowledgedDisabled = props.planOrganizationTask.status !== PlanOrganizationTaskStatus.NotStarted && coachingToolAcknowledged

  const openCommentsQuery = searchParams.get('comments')
  const activityQuery = searchParams.get('activity')

  const priorityStatusButtonRef = useRef<HTMLButtonElement>(null)
  const [priorityStatusButtonHighlighted, setPriorityStatusButtonHighlighted] = useState(false)
  const checkboxRef = useRef<HTMLButtonElement>(null)

  useEffect(() => {
    if (activityQuery != null) {
      handlePriorityActivityClick(activityQuery)
    }
  }, [activityQuery])

  const handleBackToOverview = (): void => {
    // remove activity from search params
    if (searchParams.has('activity')) {
      searchParams.delete('activity')
      setSearchParams(searchParams)
    }
    setSelectedPriorityActivity(null)
    props.onPlanOrgTaskUpdate(props.planOrganizationTask) // trigger reload of the task
  }

  const handlePriorityActivityClick = (priorityActivity: string): void => {
    if (props.planOrganizationTask.status === PlanOrganizationTaskStatus.NotStarted && (userRole === AnnualPlanRole.PlanContributor || userRole === AnnualPlanRole.PlanAdministrator)) {
      toast.error('Set the status to “In Progress” in order to continue.')
      setPriorityStatusButtonHighlighted(true)
      if (priorityStatusButtonRef.current != null) {
        priorityStatusButtonRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' })
      }
      return
    }

    setSelectedPriorityActivity(priorityActivity)
  }

  const handleBackToPriorityActivities = (): void => {
    props.onBack()
  }

  const handleUpdatePlanOrgTaskStatus = (newStatus: PlanOrganizationTaskStatus): void => {
    if (!props.implementationPhase && !coachingToolAcknowledged) {
      setShowConfirmCheckCoachingCapacityToolModal(true)
    } else {
      updatePlanOrgTaskStatus(newStatus)
    }
    setPriorityStatusButtonHighlighted(false)
  }

  const handleCheckCoachingCapacityToolModal = (): void => {
    setShowConfirmCheckCoachingCapacityToolModal(false)

    setFocusCheckbox(true)
    if (checkboxRef.current != null) {
      checkboxRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' })
    }
  }

  const DisplaySelectedActivity = (_props: { priorityActivity: string }): JSX.Element => {
    const isTracking = [
      PlanOrganizationTaskStatus.TrackingInProgress,
      PlanOrganizationTaskStatus.TrackingCompleted
    ].includes(props.planOrganizationTask.status)

    switch (_props.priorityActivity) {
      case PriorityTwoActivityName.CoachingStructureAndSystems:
        return <CSDevelopmentChecklistList
          userRole={userRole}
          priorityId={props.priority.id}
          planOrgId={planOrgId != null ? Number(planOrgId) : 0}
          readOnly={readOnly}
          trackingReadonly={trackingReadonly}
          onBack={handleBackToOverview}
          isTracking={isTracking}
          implementationPhase={props.implementationPhase}
          handleViewContract={handleNavigateToContract}
        />
      default:
        return <Typography variant='body1'>Unknown Priority</Typography>
    }
  }

  const handleCoachingToolChecked = async (_: React.ChangeEvent<HTMLInputElement>): Promise<void> => {
    const acknowledged = !coachingToolAcknowledged

    await AcknowledgeCoachingTool(props.priority.id, acknowledged)
    setCoachingToolAcknowledged(acknowledged)
    setFocusCheckbox(false)
  }

  if (isLoading) {
    return <CircularProgress />
  }

  if (error != null) {
    return <Typography variant='body1'>{error}</Typography>
  }

  return <>
    {readOnly && (
      <LockedBanner status={props.planOrganizationTask.status} />
    )}

    <Card sx={{
      backgroundColor: 'var(--grey-100)',
      borderRadius: '12px',
      padding: '20px 40px 40px 40px',
      display: 'flex',
      flexDirection: 'column',
      gap: '40px',
      position: 'relative',
      overflow: 'visible'
    }}
    >
      {selectedPriorityActivity != null
        ? <DisplaySelectedActivity priorityActivity={selectedPriorityActivity} />
        : <>
          <Box data-testid='priorityTwoOverview' sx={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Button
                data-testid='priorityActivitiesButton'
                onClick={handleBackToPriorityActivities}
                sx={{
                  padding: '5px 10px',
                  backgroundColor: 'var(--grey-50)',
                  color: 'var(--grey-500)',
                  textTransform: 'none'
                }}
              >
                <ArrowBack sx={{ mr: '10px' }} />
                Priority Activities
              </Button>

              <Box sx={{
                display: 'flex',
                alignItems: 'center',
                gap: '13px'
              }}
              >
                <PriorityStatusButton
                  ref={priorityStatusButtonRef}
                  userRole={userRole}
                  currentStatus={props.planOrganizationTask.status}
                  onChange={handleUpdatePlanOrgTaskStatus}
                  disabled={isUpdating || disableStatus}
                  highlighted={priorityStatusButtonHighlighted}
                  implementationPhase={props.implementationPhase}
                />

                <Comments
                  parentType={CommentParentType.PlanOrganizationTask}
                  parentId={props.priority.planOrganizationTaskId}
                  showComments={openCommentsQuery === 'true'}
                />
              </Box>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
              <Box sx={{
                backgroundColor: 'var(--purple-500)',
                minWidth: '91px',
                minHeight: '91px',
                display: 'grid',
                placeContent: 'center',
                borderRadius: '12px'
              }}
              >
                <Typography variant='h1' sx={{ fontSize: '40px', color: 'var(--white)', fontVariantNumeric: 'lining-nums' }}>
                  02
                </Typography>
              </Box>

              <Typography variant='h1'>
                Coaching Structure and Systems
              </Typography>
            </Box>
          </Box>

          <Box data-testid='priorityTwoGoals' sx={{ backgroundColor: 'var(--white)', border: '1px solid var(--purple-500)', borderRadius: '8px', padding: '20px' }}>
            <Typography variant='h5' color='var(--blue-500)'>Overall Goals</Typography>
            <Typography variant='body1' sx={{ m: '10px 0' }}>Develop a coaching structure within each ISD and district to support buildings and classrooms.</Typography>
            <ul style={{ margin: '0' }}>
              <li>Each ISD establishes and sustains a coaching structure and plan with coach leaders and building coaches supported by START coaching resources.</li>
              <li>The ISD develops a plan to train and support district and building coaches to use effective coaching practices for the implementation of evidence-based practices (EBP). Use the Coaching Structure Checklist as a tool to guide your planning.</li>
              <li>Disseminate and model the use of resources and tools for coaching.</li>
            </ul>
          </Box>

          <FormControlLabel
            label={'Strengthening coaching systems is a focus of Priority 2. As part of the RCN Contract, we will ask the RCN to complete the Coaching Capacity Tool. Please check this box to acknowledge this information.'}
            control={
              <Checkbox
                ref={checkboxRef}
                disabled={readOnly || coachingToolAcknowledgedDisabled}
                checked={coachingToolAcknowledged}
                onChange={handleCoachingToolChecked}
                data-testid='coachingToolCheckbox'
                sx={{
                  py: 0,
                  outline: focusCheckbox ? '-webkit-focus-ring-color auto 1px' : ''
                }}
              />
            }
          />

          <Typography variant='h2'>Core Activities</Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '26px' }}>
            {props.priority.activities.map(activity =>
              <PriorityActivityCard
                key={activity.name}
                name={activity.name}
                started={activity.started}
                backgroundColor='var(--purple-500)'
                onClick={() => { handlePriorityActivityClick(activity.name) }}
              />
            )}
          </Box>
        </>
      }

      <Modal
        open={showConfirmCheckCoachingCapacityToolModal}
        title='Are you sure?'
        cancelButtonText='Close'
        hideConfirmButton={true}
        data-testid='confirmCheckCoachingCapacityToolModal'
        bodyContent={'You must check the Coaching Capacity Tool checkbox to continue'}
        onClose={handleCheckCoachingCapacityToolModal}
        onConfirm={handleCheckCoachingCapacityToolModal}
        maxWidth='xs'
      />

      <OverviewStatusModals
        statusForLockModal={statusForLockModal}
        handleConfirmLockModal={handleConfirmLockModal}
        handleCloseLockModal={() => { setStatusForLockModal(null) }}
        statusForSomeItemsLockModal={statusForSomeItemsLockModal}
        handleConfirmNoItemsLockModal={handleConfirmLockModal}
        handleCloseNoItemsLockModal={() => { setStatusForSomeItemsLockModal(null) }}
        statusForSubmittedToInProgressModal={statusForSubmittedToInProgressModal}
        handleConfirmSubmittedToInProgressModal={handleConfirmLockModal}
        handleCloseSubmittedToInProgressModal={() => { setStatusForSubmittedToInProgressModal(null) }}
        statusForSubmittedToAcceptedModal={statusForSubmittedToAcceptedModal}
        handleConfirmSubmittedToAcceptedModal={handleConfirmLockModal}
        handleCloseSubmittedToAcceptedModal={() => { setStatusForSubmittedToAcceptedModal(null) }}
        statusForSubmittedToNeedsRevisionModal={statusForSubmittedToNeedsRevisionModal}
        handleConfirmSubmittedToNeedsRevision={handleConfirmSubmittedToNeedsRevision}
        handleCloseSubmittedToNeedsRevision={() => {
          setStatusForSubmittedToNeedsRevisionModal(null)
        }}
        activities={props.priority.activities}
        planOrganizationTaskId={props.planOrganizationTask.id}
        priorityNumber={props.priority.priorityNumber}
      />
    </Card>
  </>
}
